// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, ScanFace, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type PiiMarkerEntry, type PiiMarkersSortBy, usePiiMarkersQuery } from "@/api/hooks/use-pii-marker";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { UserLink } from "@/components/shared/user-link";
import { Button } from "@/components/ui/button";
import { PiiMarkerActionsMenu } from "@/features/hardware/pii-marker/pii-marker-actions-menu";
import {
  PiiMarkerDeleteDialog,
  type PiiMarkerDeleteTarget,
} from "@/features/hardware/pii-marker/pii-marker-delete-dialog";
import { PiiMarkerDialog, type PiiMarkerTarget } from "@/features/hardware/pii-marker/pii-marker-dialog";
import {
  PiiMarkerToggleDialog,
  type PiiMarkerToggleTarget,
} from "@/features/hardware/pii-marker/pii-marker-toggle-dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { getDefaultPageSize } from "@/lib/preferences";

export function PiiMarkerList({
  initialTarget,
  onDialogClosed,
}: {
  initialTarget?: PiiMarkerTarget;
  /** Lets the route drop a deep-link search param once the dialog it opened is closed. */
  onDialogClosed?: () => void;
} = {}) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.hardwarePiiMarkerEdit);

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: getDefaultPageSize() });
  const [sorting, setSorting] = useState<SortingState>([{ id: "updatedAt", desc: true }]);
  const [search, setSearch] = useState("");
  const [chainFilter, setChainFilter] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [toggleTarget, setToggleTarget] = useState<PiiMarkerToggleTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PiiMarkerDeleteTarget | null>(null);
  // undefined target = the dialog starts at the choose step. A deep link (`?image=` from support
  // tickets, `?camera=`) opens straight in that editor.
  const [dialog, setDialog] = useState<{ open: boolean; target?: PiiMarkerTarget }>(() =>
    initialTarget ? { open: true, target: initialTarget } : { open: false },
  );

  const { data: context, isLoading: contextLoading } = useBackofficeContext();
  // Full store list for the dropdown, scoped to the selected chain; the dropdown is virtualized
  const { data: storesData, isLoading: storesLoading } = useStoresQuery({
    chainName: chainFilter || undefined,
    limit: -1,
  });
  const storeOptions = useMemo(
    () =>
      [...(storesData?.stores ?? [])]
        .sort((a, b) => a.legibleName.localeCompare(b.legibleName))
        .map((s) => ({ label: s.legibleName, value: s.storeName })),
    [storesData?.stores],
  );

  const isFiltered = !!(search || chainFilter || storeFilter || !onlyActive);
  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setChainFilter("");
    setStoreFilter("");
    setOnlyActive(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);
  useEscapeToClearFilters({ isFiltered, clearFilters });

  const { data, isLoading, refetch, isFetching } = usePiiMarkersQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy: (sorting[0]?.id ?? "updatedAt") as PiiMarkersSortBy,
    orderBy: sorting[0]?.desc ? "desc" : "asc",
    chainName: chainFilter || undefined,
    storeName: storeFilter || undefined,
    onlyActive,
  });

  const chainMap = useMemo(() => new Map((data?.chains ?? []).map((c) => [c.chainId, c])), [data?.chains]);
  const storeMap = useMemo(() => new Map((data?.stores ?? []).map((s) => [s.storeId, s])), [data?.stores]);
  const userMap = useMemo(() => new Map((data?.users ?? []).map((u) => [u.userId, u])), [data?.users]);

  const columns = useMemo<DataTableColumnDef<PiiMarkerEntry>[]>(
    () => [
      {
        accessorKey: "cameraId",
        header: t("piiMarker.list.columns.camera"),
        size: 100,
        enableHiding: false,
        meta: {
          columnLabel: t("piiMarker.list.columns.camera"),
          className: "pl-4",
          forceWidth: true,
          sticky: "left",
        },
        cell: ({ row }) => <RowId id={row.original.cameraId} />,
      },
      {
        accessorKey: "deviceName",
        header: t("piiMarker.list.columns.device"),
        meta: { columnLabel: t("piiMarker.list.columns.device") },
        cell: ({ row }) => (
          <button
            type="button"
            className="w-fit font-mono font-medium text-primary hover:underline"
            onClick={() => setDialog({ open: true, target: { cameraId: row.original.cameraId } })}
          >
            {row.original.deviceName ?? `#${row.original.cameraId}`}
          </button>
        ),
      },
      {
        id: "store",
        header: t("piiMarker.list.columns.store"),
        enableSorting: false,
        meta: { columnLabel: t("piiMarker.list.columns.store") },
        cell: ({ row }) => {
          const store = row.original.storeId != null ? storeMap.get(row.original.storeId) : undefined;
          return store ? <StoreLink storeName={store.storeName} legibleName={store.storeDisplay} /> : "—";
        },
      },
      {
        id: "chain",
        header: t("piiMarker.list.columns.chain"),
        enableSorting: false,
        meta: { columnLabel: t("piiMarker.list.columns.chain") },
        cell: ({ row }) => {
          const chain = row.original.chainId != null ? chainMap.get(row.original.chainId) : undefined;
          return chain ? <ChainLink chainName={chain.chainName} legibleName={chain.legibleName} /> : "—";
        },
      },
      {
        accessorKey: "regionCount",
        header: t("piiMarker.list.columns.regions"),
        size: 100,
        meta: { columnLabel: t("piiMarker.list.columns.regions") },
        cell: ({ row }) => <span className="tabular-nums">{row.original.regionCount}</span>,
      },
      {
        id: "user",
        header: t("piiMarker.list.columns.updatedBy"),
        enableSorting: false,
        meta: { columnLabel: t("piiMarker.list.columns.updatedBy") },
        cell: ({ row }) => {
          const user = row.original.userId != null ? userMap.get(row.original.userId) : undefined;
          return user ? <UserLink publicId={user.publicId} legibleName={user.fullName || user.email} /> : "—";
        },
      },
      {
        accessorKey: "updatedAt",
        header: t("piiMarker.list.columns.updatedAt"),
        sortDescFirst: true,
        meta: { columnLabel: t("piiMarker.list.columns.updatedAt") },
        cell: ({ row }) => <DateTimeValue value={row.original.updatedAt} />,
      },
      {
        accessorKey: "createdAt",
        header: t("piiMarker.list.columns.createdAt"),
        meta: { columnLabel: t("piiMarker.list.columns.createdAt"), defaultHidden: true },
        cell: ({ row }) => <DateTimeValue value={row.original.createdAt} />,
      },
      {
        id: "isActive",
        header: t("piiMarker.list.columns.status"),
        size: 80,
        enableSorting: false,
        meta: { columnLabel: t("piiMarker.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      ...(canEdit
        ? [
            {
              id: "actions",
              size: 50,
              meta: { forceWidth: true, sticky: "right" },
              enableSorting: false,
              enableHiding: false,
              cell: ({ row }: { row: { original: PiiMarkerEntry } }) => (
                <PiiMarkerActionsMenu
                  isActive={row.original.isActive}
                  canActivate={row.original.regionCount > 0}
                  onToggleActive={() =>
                    setToggleTarget({
                      cameraId: row.original.cameraId,
                      label: row.original.deviceName ?? `#${row.original.cameraId}`,
                      isActive: row.original.isActive,
                    })
                  }
                  onDelete={() =>
                    setDeleteTarget({
                      cameraId: row.original.cameraId,
                      label: row.original.deviceName ?? `#${row.original.cameraId}`,
                    })
                  }
                />
              ),
            } satisfies DataTableColumnDef<PiiMarkerEntry>,
          ]
        : []),
    ],
    [t, storeMap, chainMap, userMap, canEdit],
  );

  return (
    <div>
      <PageHeader title={t("piiMarker.title")} />
      <div className="p-6">
        <DataTable
          storageKey="pii-marker-list"
          columns={columns}
          data={data?.masks ?? []}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={500}
          onRefresh={refetch}
          isRefreshing={isFetching}
          searchPlaceholder={t("piiMarker.list.searchPlaceholder")}
          filters={
            <>
              <FilterSelect
                label={t("piiMarker.list.columns.chain")}
                value={chainFilter}
                onChange={(v) => {
                  setChainFilter(v);
                  setStoreFilter("");
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                searchPlaceholder={t("stores.form.searchChains")}
                emptyText={t("stores.form.noChains")}
                loading={contextLoading}
              />
              <FilterSelect
                label={t("piiMarker.list.columns.store")}
                value={storeFilter}
                onChange={(v) => {
                  setStoreFilter(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={storeOptions}
                searchPlaceholder={t("stores.operations.searchOption")}
                emptyText={t("stores.operations.noOption")}
                loading={storesLoading}
              />
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
          toolbar={
            canEdit ? (
              <Button onClick={() => setDialog({ open: true })}>
                <ScanFace className="h-4 w-4" />
                {t("piiMarker.list.createButton")}
              </Button>
            ) : null
          }
        />
      </div>

      <PiiMarkerToggleDialog target={toggleTarget} onClose={() => setToggleTarget(null)} />
      <PiiMarkerDeleteDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} />
      <PiiMarkerDialog
        open={dialog.open}
        target={dialog.target}
        onOpenChange={(open) => {
          setDialog((prev) => ({ ...prev, open }));
          if (!open) onDialogClosed?.();
        }}
      />
    </div>
  );
}
